import React, { useEffect, useState, useMemo } from 'react';
import { useField } from 'formik';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useSnackbar } from 'notistack';

import objectsClient from '../../../../../../clients/objects';

const toSelectedIds = (val) =>
  Array.isArray(val) ? val.map((v) => (typeof v === 'object' && v != null && 'id' in v ? v.id : v)) : [];

const useStyles = makeStyles((theme) => ({
  searchField: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

const ObjectsSelectField = ({ name, label, fullWidth, ...props }) => {
  const classes = useStyles();
  const [field, meta, { setValue }] = useField(name);
  const { enqueueSnackbar } = useSnackbar();
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const value = field.value;
  const selectedIds = toSelectedIds(value);

  const filteredObjects = useMemo(() => {
    if (!searchValue) {
      return objects;
    }
    const searchLower = searchValue.toLowerCase();
    return objects.filter((obj) =>
      (obj.Title || '').toLowerCase().includes(searchLower),
    );
  }, [objects, searchValue]);

  // Normalize form value to ids when API returns populated objects
  useEffect(() => {
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.some((v) => typeof v === 'object' && v != null && 'id' in v)
    ) {
      setValue(toSelectedIds(value));
    }
  }, [value, setValue]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await objectsClient.find({
          sort: 'createdAt:desc',
        });
        setObjects(data || []);
      } catch (error) {
        console.error('Error fetching objects:', error);
        enqueueSnackbar(
          'Nepavyko gauti objektų sąrašo. Bandykite atidaryti iš naujo.',
          { variant: 'error' }
        );
        setObjects([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [enqueueSnackbar]);

  const handleChange = (event) => {
    const next = event.target.value;
    setValue(Array.isArray(next) ? toSelectedIds(next) : next);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue('');
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const renderValue = (selected) => {
    const ids = toSelectedIds(selected);
    const selectedObjects = ids
      .map((id) => objects.find((o) => o.id === id))
      .filter(Boolean);
    return (
      <Box className={classes.chips}>
        {selectedObjects.map((obj) => (
          <Chip key={obj.id} label={obj.Title || ''} size="small" />
        ))}
      </Box>
    );
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <FormControl
      variant="outlined"
      fullWidth={fullWidth}
      error={meta.touched && !!meta.error}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        {...field}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleChange}
        value={selectedIds}
        label={label}
        multiple
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        {...props}
      >
        <div
          style={{
            height: 'auto',
            padding: 12,
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 100,
            width: '100%',
          }}
        >
          <TextField
            placeholder="Ieškoti..."
            value={searchValue}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            className={classes.searchField}
            autoFocus
          />
        </div>
        {filteredObjects.map(({ id, Title }) => (
          <MenuItem key={id} value={id}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={selectedIds.indexOf(id) !== -1}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={Title} />
          </MenuItem>
        ))}
      </Select>
      {meta.touched && meta.error && (
        <FormHelperText>{meta.error}</FormHelperText>
      )}
    </FormControl>
  );
};

export default ObjectsSelectField;
